import {
  DataTexture,
  DoubleSide,
  FloatType,
  MeshBasicMaterial,
  NearestFilter,
  RedFormat,
  RGBAFormat,
  UnsignedByteType,
  Vector2,
} from 'three';

import { curveSteps, floatsPerItem, itemsPerRow, StyledLinesData } from './styled-lines-data';
import { StripePattern } from './styled-segment';

/**
 * The maximum number of lengths in a stripe pattern, longer patterns are cut.
 */
export const maxPatternLengths = 16;

/**
 * Vertex shader of the styled lines. The items are read from the item texture (see
 * styled-lines-data.ts), each vertex has the index of its item and its corner (position.x, y).
 *
 * The corners of a segment are numbered in pairs, on either side of it at each step along it.
 * A curve has corners at curveSteps steps, of which the triangles only use as many as it needs on
 * screen, see StyledLines.updateCurveSteps.
 * A band lies in the horizontal plane, perpendicular to the segment. When it is narrower than the
 * minimum width on screen, e.g. a line without a width or a band seen from the side, it is drawn
 * facing the camera instead, the minimum width wide.
 *
 * The curve calculations are the same as in conic.ts, keep them in sync.
 */
const vertexShaderFunctions = /* glsl */ `
uniform sampler2D lineItems;
uniform vec2 lineResolution;
uniform float lineMinWidth;
uniform bool lineAnyHidden;
uniform sampler2D lineHidden;

varying float vLineDistance;
varying float vLineAcross;
varying float vLineHalfWidth;
varying vec3 vLineStripe;
varying float vLineDot;
varying vec4 vLineColor;

vec3 lineStart;
vec3 lineEnd;
vec3 lineControl;
float lineWeight;

vec4 lineItem(int item, int texel) {
  return texelFetch(
    lineItems,
    ivec2((item % ${itemsPerRow}) * ${floatsPerItem / 4} + texel, item / ${itemsPerRow}),
    0
  );
}

vec4 lineToClip(vec3 p) {
  return projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}

vec2 lineToScreen(vec4 clip) {
  return clip.xy / clip.w * 0.5 * lineResolution;
}

/**
 * The size of a pixel at the point, in the units of the positions.
 */
float lineWorldPerPixel(vec4 clip) {
  float modelScale = length(modelMatrix[0].xyz);
  return 2.0 * clip.w / (projectionMatrix[1][1] * lineResolution.y) / modelScale;
}

vec3 lineSRGBToLinear(vec3 color) {
  return mix(
    color / 12.92,
    pow((color + 0.055) / 1.055, vec3(2.4)),
    step(vec3(0.04045), color)
  );
}

vec3 conicPoint(float t) {
  float a = (1.0 - t) * (1.0 - t);
  float b = 2.0 * t * (1.0 - t) * lineWeight;
  float c = t * t;
  return (a * lineStart + b * lineControl + c * lineEnd) / (a + b + c);
}

vec3 conicDerivative(float t) {
  float a = (1.0 - t) * (1.0 - t);
  float b = 2.0 * t * (1.0 - t) * lineWeight;
  float c = t * t;
  float denominator = a + b + c;
  float denominatorDerivative = 2.0 * (1.0 - t) * (lineWeight - 1.0) + 2.0 * t * (1.0 - lineWeight);
  vec3 numerator = a * lineStart + b * lineControl + c * lineEnd;
  vec3 numeratorDerivative =
    2.0 * (1.0 - t) * (lineWeight * lineControl - lineStart) +
    2.0 * t * (lineEnd - lineWeight * lineControl);
  return (numeratorDerivative * denominator - numerator * denominatorDerivative) /
    (denominator * denominator);
}

/**
 * The length along the curve from its start to t, by 5 point Gauss-Legendre.
 */
float conicLength(float t) {
  float halfT = 0.5 * t;
  float x[5] = float[](0.0, -0.5384693101056831, 0.5384693101056831, -0.906179845938664, 0.906179845938664);
  float w[5] = float[](0.5688888888888889, 0.4786286704993665, 0.4786286704993665, 0.2369268850561891, 0.2369268850561891);
  float sum = 0.0;

  for (int i = 0; i < 5; i++) {
    sum += w[i] * length(conicDerivative(halfT * (x[i] + 1.0)));
  }

  return sum * halfT;
}

/**
 * Places the vertex and sets the varyings, returns the position in clip space.
 * The position in the units of the positions is written to point, for the clipping planes.
 */
vec4 getStyledLinePosition(out vec3 point) {
  int item = int(position.x + 0.5);
  int corner = int(position.y + 0.5);

  vec4 texel0 = lineItem(item, 0);
  vec4 texel1 = lineItem(item, 1);
  vec4 texel2 = lineItem(item, 2);
  vec4 texel3 = lineItem(item, 3);

  lineStart = texel0.xyz;
  lineWeight = texel0.w;
  lineEnd = texel1.xyz;
  float lineWidth = texel1.w;
  lineControl = texel2.xyz;

  float packedColor = texel2.w;
  vec3 color = vec3(
    floor(packedColor / 65536.0),
    mod(floor(packedColor / 256.0), 256.0),
    mod(packedColor, 256.0)
  ) / 255.0;

  vLineColor = vec4(lineSRGBToLinear(color), texel3.w);
  vLineStripe = texel3.xyz;
  vLineDot = 0.0;
  vLineDistance = 0.0;
  vLineAcross = 0.0;
  vLineHalfWidth = 1.0;

  // A hidden item, e.g. on a hidden layer: all of its corners outside of the view.
  if (lineAnyHidden && texelFetch(lineHidden, ivec2(item % ${itemsPerRow}, item / ${itemsPerRow}), 0).r > 0.0) {
    point = lineStart;
    return vec4(0.0, 0.0, 2.0, 1.0);
  }

  // A triangle: the start, end and control point are its corners.
  if (lineWeight < -0.5) {
    point = corner == 0 ? lineStart : corner == 1 ? lineEnd : lineControl;
    vLineStripe = vec3(-1.0, 1.0, 0.0);
    return lineToClip(point);
  }

  float side = corner % 2 == 0 ? -1.0 : 1.0;
  float stepIndex = float(corner / 2);
  float minHalfWidth = lineMinWidth * 0.5;
  bool curved = lineWeight > 0.0;

  // A dot: a disc of the width, drawn on a square in the horizontal plane.
  if (!curved && length(lineEnd - lineStart) < 1e-12) {
    float along = stepIndex > 0.5 ? 1.0 : -1.0;
    vec4 clip = lineToClip(lineStart);
    float halfWidth = max(lineWidth * 0.5, minHalfWidth * lineWorldPerPixel(clip));

    vLineDot = 1.0;
    vLineDistance = along;
    vLineAcross = side;
    vLineHalfWidth = halfWidth;
    point = lineStart + vec3(along, side, 0.0) * halfWidth;

    return lineToClip(point);
  }

  float t = curved ? stepIndex / ${curveSteps.toFixed(1)} : stepIndex;

  vec3 center = curved ? conicPoint(t) : mix(lineStart, lineEnd, t);
  vec3 tangent = curved ? conicDerivative(t) : lineEnd - lineStart;
  // The length along a curve is only needed for its stripes.
  vLineDistance = !curved
    ? t * length(lineEnd - lineStart)
    : vLineStripe.x >= 0.0
      ? conicLength(t)
      : 0.0;
  vLineAcross = side;

  vec4 centerClip = lineToClip(center);
  float worldPerPixel = lineWorldPerPixel(centerClip);
  float halfWidth = lineWidth * 0.5;
  bool horizontal = length(tangent.xy) > 1e-9 * length(tangent);

  // The band in the horizontal plane, unless it is too narrow on screen or the segment is vertical.
  if (horizontal && halfWidth > 0.0) {
    vec2 direction = normalize(tangent.xy);
    point = center + vec3(direction.y, -direction.x, 0.0) * halfWidth * side;
    vec4 clip = lineToClip(point);
    float pixels = length(lineToScreen(clip) - lineToScreen(centerClip));

    if (pixels >= minHalfWidth) {
      vLineHalfWidth = halfWidth;
      return clip;
    }
  }

  // Facing the camera, perpendicular to the segment on screen.
  float halfWidthPixels = max(minHalfWidth, halfWidth / worldPerPixel);
  vec3 lineTangent = length(tangent) > 0.0 ? normalize(tangent) : vec3(1.0, 0.0, 0.0);
  vec2 screenDirection =
    lineToScreen(lineToClip(center + lineTangent * worldPerPixel)) - lineToScreen(centerClip);
  screenDirection = length(screenDirection) > 1e-6 ? normalize(screenDirection) : vec2(1.0, 0.0);

  vec2 offset = vec2(-screenDirection.y, screenDirection.x) * halfWidthPixels * side;
  vLineHalfWidth = halfWidthPixels * worldPerPixel;
  point = center;

  return centerClip + vec4(offset / (0.5 * lineResolution) * centerClip.w, 0.0, 0.0);
}
`;

const vertexShaderMain = /* glsl */ `
vec3 styledLinePoint;
vec4 styledLinePosition = getStyledLinePosition(styledLinePoint);
vec3 transformed = styledLinePoint;
`;

/**
 * Leaves out the gaps of the stripes, and what is outside of the dots.
 */
const fragmentShaderFunctions = /* glsl */ `
uniform sampler2D linePatterns;

varying float vLineDistance;
varying float vLineAcross;
varying float vLineHalfWidth;
varying vec3 vLineStripe;
varying float vLineDot;
varying vec4 vLineColor;

bool isStyledLineDrawn() {
  if (vLineDot > 0.5) {
    return vLineDistance * vLineDistance + vLineAcross * vLineAcross <= 1.0;
  }

  if (vLineStripe.x < 0.0) {
    return true;
  }

  int pattern = int(vLineStripe.x + 0.5);
  float scale = vLineStripe.y;
  int count = int(texelFetch(linePatterns, ivec2(0, pattern), 0).r + 0.5);
  float total = texelFetch(linePatterns, ivec2(1, pattern), 0).r * scale;

  if (total <= 0.0) {
    return true;
  }

  float u = mod(vLineStripe.z + vLineDistance, total);
  float across = vLineAcross * vLineHalfWidth;
  float start = 0.0;

  for (int i = 0; i < ${maxPatternLengths}; i++) {
    if (i >= count) {
      break;
    }

    float dashLength = texelFetch(linePatterns, ivec2(2 + i, pattern), 0).r * scale;

    if (i % 2 == 0) {
      if (dashLength > 0.0 && u >= start && u < start + dashLength) {
        return true;
      }

      // A dot: a disc of the width.
      if (dashLength == 0.0) {
        float dotDistance = abs(u - start);
        dotDistance = min(dotDistance, total - dotDistance);

        if (dotDistance * dotDistance + across * across <= vLineHalfWidth * vLineHalfWidth) {
          return true;
        }
      }
    }

    start += dashLength;
  }

  return false;
}
`;

/**
 * Creates the texture with the items, see styled-lines-data.ts.
 */
export function createItemTexture(data: StyledLinesData): DataTexture {
  const texelsPerItem = floatsPerItem / 4;
  const width = itemsPerRow * texelsPerItem;
  const height = Math.max(1, Math.ceil(data.count / itemsPerRow));
  const texels = new Float32Array(width * height * 4);
  texels.set(data.items.subarray(0, data.count * floatsPerItem));

  const texture = new DataTexture(texels, width, height, RGBAFormat, FloatType);
  texture.minFilter = NearestFilter;
  texture.magFilter = NearestFilter;
  texture.needsUpdate = true;

  return texture;
}

/**
 * Creates the texture with whether each item is hidden (not 0) or not (0), laid out like the items.
 */
export function createHiddenTexture(hidden: Uint8Array): DataTexture {
  const height = Math.max(1, Math.ceil(hidden.length / itemsPerRow));
  const texels = new Uint8Array(itemsPerRow * height);
  texels.set(hidden);

  const texture = new DataTexture(texels, itemsPerRow, height, RedFormat, UnsignedByteType);
  texture.minFilter = NearestFilter;
  texture.magFilter = NearestFilter;
  texture.needsUpdate = true;

  return texture;
}

/**
 * Creates the texture with the stripe patterns. Each row is a pattern: the number of lengths, their
 * sum, and the lengths.
 */
export function createPatternTexture(patterns: StripePattern[]): DataTexture {
  const width = maxPatternLengths + 2;
  const height = Math.max(patterns.length, 1);
  const data = new Float32Array(width * height);

  patterns.forEach((pattern, row) => {
    const lengths = pattern.slice(0, maxPatternLengths);
    data[row * width] = lengths.length;
    data[row * width + 1] = lengths.reduce((sum, length) => sum + length, 0);
    lengths.forEach((length, i) => (data[row * width + 2 + i] = length));
  });

  const texture = new DataTexture(data, width, height, RedFormat, FloatType);
  texture.minFilter = NearestFilter;
  texture.magFilter = NearestFilter;
  texture.needsUpdate = true;

  return texture;
}

/**
 * Creates the material of styled lines. It is a MeshBasicMaterial whose vertex shader places the
 * vertices along the items, so color, opacity, clipping planes etc. work as usual.
 * The color and opacity are multiplied with those of each item.
 */
export function createStyledLinesMaterial(uniforms: {
  lineItems: { value: DataTexture };
  lineResolution: { value: Vector2 };
  lineMinWidth: { value: number };
  linePatterns: { value: DataTexture };
  lineAnyHidden: { value: boolean };
  lineHidden: { value: DataTexture };
}): MeshBasicMaterial {
  const material = new MeshBasicMaterial({ side: DoubleSide });

  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${vertexShaderFunctions}`)
      .replace('#include <begin_vertex>', vertexShaderMain)
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\ngl_Position = styledLinePosition;',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${fragmentShaderFunctions}`)
      .replace(
        '#include <clipping_planes_fragment>',
        '#include <clipping_planes_fragment>\nif (!isStyledLineDrawn()) discard;',
      )
      .replace(
        '#include <color_fragment>',
        '#include <color_fragment>\ndiffuseColor *= vLineColor;',
      );
  };
  material.customProgramCacheKey = () => 'styled-lines';

  return material;
}
